import { Navigate, Outlet, createFileRoute, retainSearchParams, stripSearchParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';

import { AppSkeletonLoader } from '@/components/AppSkeletonLoader';
import { RequireFeatureFlag } from '@/components/Auth/RequireFeatureFlag';
import { InvestigationsPaneProvider } from '@/components/Investigations/InvestigationsPaneContext';
import { INVESTIGATIONS_SEARCH_DEFAULTS, investigationsSearchSchema } from '@/components/Investigations/searchSchema';
import { NotFound404 } from '@/components/NotFound404';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { InvestigationStreamProvider } from '@/contexts/InvestigationStreamContext';
import { useUser } from '@/hooks/useUser';

// The list pane's search and status filter live here rather than on a child
// route: every investigations page shows the pane, so the filter has to
// survive moving between them. `retainSearchParams` carries it onto every link
// inside the section without each link having to pass it along. Strip is
// listed first so it runs last, after retain has carried the defaults over.
export const Route = createFileRoute('/$organizationName/investigations')({
  validateSearch: zodValidator(investigationsSearchSchema),
  search: {
    middlewares: [stripSearchParams(INVESTIGATIONS_SEARCH_DEFAULTS), retainSearchParams(['q', 'status'])],
  },
  component: GatedInvestigationsLayout,
  notFoundComponent: () => <NotFound404 />,
});

function GatedInvestigationsLayout() {
  const { routeOrganizationName } = useUser();

  return (
    <RequireFeatureFlag
      flag={FEATURE_FLAGS.SUPPORT_AGENT}
      loading={<AppSkeletonLoader />}
      redirect={
        <Navigate to="/$organizationName/sources" replace params={{ organizationName: routeOrganizationName }} />
      }
    >
      <InvestigationStreamProvider>
        <InvestigationsPaneProvider>
          <Outlet />
        </InvestigationsPaneProvider>
      </InvestigationStreamProvider>
    </RequireFeatureFlag>
  );
}
